import { useState, useRef, useCallback, useEffect } from "react";
import {
  FieldFormData,
  FieldFormState,
  OptionsManagerData,
  initializeFieldFormData,
  validateFieldForm,
  buildFieldConfig
} from "../utils/fieldFormUtils";
import {
  APIFieldItem,
  FieldDataSource,
  SettingsFieldType
} from "@/pages/settings/types";
import { FieldDisplayType } from "../types";
import {
  EntityType,
  entityTypeToEntityName
} from "@/services/hooks/entities/useEntityType";
import {
  useCreateEntityField,
  useUpdateEntityField,
  CreateFieldRequest,
  UpdateFieldRequest
} from "@/pages/settings/hooks";

interface UseFieldFormProps {
  fieldData?: APIFieldItem | null;
  entityType?: EntityType;
  isEditMode?: boolean;
  isPresetFieldEdit?: boolean;
}

interface UseFieldFormReturn {
  formState: FieldFormState;
  currentFieldData: APIFieldItem | null;
  isFormValid: boolean;
  formRef: React.RefObject<HTMLFormElement>;
  formSetValue: any;
  setFormData: (data: FieldFormData) => void;
  setOptionsManagerData: (data: OptionsManagerData) => void;
  setIsRequired: (required: boolean) => void;
  setShowInCreation: (show: boolean) => void;
  setShowHelpText: (show: boolean) => void;
  setFormSetValue: (setValue: any) => void;
  handleFormValid: (valid: boolean, data: any) => void;
  handleOptionsManagerChange: (data: OptionsManagerData) => void;
  handleFilledByDreamerChange: (filledByDreamer: boolean) => void;
  handleFormSubmit: () => Promise<any>;
  handleSubmitButton: () => void;
  isSubmitting: boolean;
  submitError: Error | null;
}

export const useFieldForm = ({
  fieldData,
  entityType = EntityType.DEAL,
  isEditMode = false,
  isPresetFieldEdit = false
}: UseFieldFormProps): UseFieldFormReturn => {
  const formRef = useRef<HTMLFormElement>(null);
  const [isFormValid, setIsFormValid] = useState(false);
  const [formSetValue, setFormSetValue] = useState<any>(null);
  const [filledByDreamer, setFilledByDreamer] = useState<boolean>(
    fieldData?.filledByDreamer ?? false
  );

  const [formState, setFormState] = useState<FieldFormState>(() =>
    initializeFieldFormData(fieldData || null, isPresetFieldEdit)
  );

  const createFieldMutation = useCreateEntityField(entityType);
  const updateFieldMutation = useUpdateEntityField(entityType);

  useEffect(() => {
    setFormState(initializeFieldFormData(fieldData || null, isPresetFieldEdit));
    setFilledByDreamer(fieldData?.filledByDreamer ?? false);
  }, [fieldData, isPresetFieldEdit]);

  useEffect(() => {
    if (!formSetValue?.setValue) return;
    if (formState.formData.fieldType !== FieldDisplayType.CURRENCY) return;
    const current = formSetValue.getValues?.("defaultValue");
    const next = formState.optionsManagerData.defaultValue;
    if (current !== next) {
      formSetValue.setValue("defaultValue", next);
    }
  }, [formState.formData.fieldType, formState.optionsManagerData.defaultValue, formSetValue]);

  const setFormData = useCallback((data: FieldFormData) => {
    setFormState((prev) => ({ ...prev, formData: data }));
  }, []);

  const setOptionsManagerData = useCallback((data: OptionsManagerData) => {
    setFormState((prev) => ({ ...prev, optionsManagerData: data }));
  }, []);

  const setIsRequired = useCallback((required: boolean) => {
    setFormState((prev) => ({ ...prev, isRequired: required }));
  }, []);

  const setShowInCreation = useCallback((show: boolean) => {
    // A field hidden from the creation form can't be required.
    setFormState((prev) => ({
      ...prev,
      showInCreation: show,
      isRequired: show ? prev.isRequired : false
    }));
  }, []);

  const setShowHelpText = useCallback((show: boolean) => {
    setFormState((prev) => ({ ...prev, showHelpText: show }));
  }, []);

  const handleFormValid = useCallback(
    (valid: boolean, data: any) => {
      const isValid = validateFieldForm(
        data,
        formState.optionsManagerData,
        valid,
        isPresetFieldEdit
      );
      setIsFormValid(isValid);
      setFormData(data);
    },
    [formState.optionsManagerData, setFormData, isPresetFieldEdit]
  );

  const handleOptionsManagerChange = useCallback(
    (data: OptionsManagerData) => {
      setOptionsManagerData(data);
    },
    [setOptionsManagerData]
  );

  const handleFilledByDreamerChange = useCallback(
    (newFilledByDreamer: boolean) => {
      setFilledByDreamer(newFilledByDreamer);
    },
    []
  );

  const currentFieldData = fieldData
    ? {
        ...fieldData,
        filledByDreamer
      }
    : null;

  const isSubmitting = isEditMode
    ? updateFieldMutation.isPending
    : createFieldMutation.isPending;

  const handleFormSubmit = useCallback(async () => {
    // Prevent multiple concurrent submissions
    if (isSubmitting) {
      throw new Error("Form is already being submitted");
    }

    const config = buildFieldConfig(
      formState.formData,
      formState.optionsManagerData
    );

    try {
      if (isEditMode && fieldData) {
        const apiRequest: UpdateFieldRequest = {
          id: fieldData.id,
          label: formState.formData.fieldLabel,
          displayType: formState.formData.fieldType,
          fieldType: isPresetFieldEdit
            ? fieldData.fieldType
            : SettingsFieldType.CUSTOM,
          dataSource: isPresetFieldEdit
            ? fieldData.dataSource
            : FieldDataSource.MANUAL,
          required: isPresetFieldEdit
            ? fieldData.required
            : formState.isRequired,
          showInCreation: isPresetFieldEdit
            ? fieldData.showInCreation
            : formState.showInCreation,
          showInDetailedView: true,
          filledByDreamer: isPresetFieldEdit ? filledByDreamer : false,
          dreamerFlow: isPresetFieldEdit ? fieldData.dreamerFlow : null,
          helpText: formState.formData.fieldHelpText || null,
          config
        };

        return await updateFieldMutation.mutateAsync({
          data: apiRequest,
          pathParams: {
            entityType: entityTypeToEntityName[entityType],
            fieldId: fieldData.id
          }
        });
      } else {
        const apiRequest: CreateFieldRequest = {
          label: formState.formData.fieldLabel,
          displayType: formState.formData.fieldType,
          fieldType: SettingsFieldType.CUSTOM,
          dataSource: FieldDataSource.MANUAL,
          required: formState.isRequired,
          showInCreation: formState.showInCreation,
          showInDetailedView: true,
          filledByDreamer: false,
          dreamerFlow: null,
          helpText: formState.formData.fieldHelpText || null,
          readOnly: false,
          config
        };

        return await createFieldMutation.mutateAsync(apiRequest);
      }
    } catch (error) {
      throw error;
    }
  }, [
    formState,
    fieldData,
    filledByDreamer,
    isEditMode,
    isPresetFieldEdit,
    createFieldMutation,
    updateFieldMutation,
    isSubmitting
  ]);

  const handleSubmitButton = useCallback(() => {
    formRef.current?.requestSubmit();
  }, []);

  const submitError = isEditMode
    ? updateFieldMutation.error
    : createFieldMutation.error;

  return {
    formState,
    currentFieldData,
    isFormValid,
    formRef,
    formSetValue,
    setFormData,
    setOptionsManagerData,
    setIsRequired,
    setShowInCreation,
    setShowHelpText,
    setFormSetValue,
    handleFormValid,
    handleOptionsManagerChange,
    handleFilledByDreamerChange,
    handleFormSubmit,
    handleSubmitButton,
    isSubmitting,
    submitError
  };
};
